import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { NavLink as RouterLink, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Toast from "../../components/Layout/Toast";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import { changePageTitle } from "../preferences/preferencesSlice";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import {
  fetchComplaint,
  selectComplaints,
  updateComplaintStatus,
} from "./complaintSlice";
import { UpdateComplaintStatusInput } from "./types/complaintTypes";
import { ComplaintStatusActions } from "./components/ComplaintStatusActions";
import { ComplaintStatusChip } from "./components/ComplaintStatusChip";
import ComplaintFollowUp from "./components/ComplaintFollowUp";
import { ComplainStatus } from "./types/complaintTypes";

export const ComplaintDetail = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const roles = user?.roles ?? [];
  const { selectedComplaint, loading, success, error } =
    useAppSelector(selectComplaints);

  const canEdit =
    isPrivilegedTransaction(roles, RoleTypes.EditComplains) &&
    selectedComplaint &&
    selectedComplaint.status !== ComplainStatus.closed &&
    selectedComplaint.status !== ComplainStatus.confirmed;

  useEffect(() => {
    dispatch(changePageTitle(t("complaintDetail")));
  }, [dispatch, t]);

  useEffect(() => {
    if (id) {
      dispatch(fetchComplaint(id));
    }
  }, [dispatch, id]);

  const handleStatusChange = (input: UpdateComplaintStatusInput) => {
    dispatch(updateComplaintStatus(input)).then((result) => {
      if (updateComplaintStatus.fulfilled.match(result) && id) {
        dispatch(fetchComplaint(id));
      }
    });
  };

  if (loading === "pending" && !selectedComplaint) {
    return (
      <Container sx={{ py: 3 }}>
        <TableSkeleton numRows={4} numColumns={2} />
      </Container>
    );
  }

  if (!selectedComplaint) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography>{t("complaintNotFound")}</Typography>
      </Container>
    );
  }

  const c = selectedComplaint;

  return (
    <>
      <Helmet>
        <title>{c.subject} | {t("complaints")} | CvPlus</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">{c.subject}</Typography>
              <ComplaintStatusChip status={c.status} />
            </Box>
            <Stack direction="row" spacing={1}>
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  component={RouterLink}
                  to={`/app/complaints/${id}/edit`}
                >
                  {t("edit")}
                </Button>
              )}
              <Button component={RouterLink} to="/app/complaints">
                {t("backToList")}
              </Button>
            </Stack>
          </Stack>

          <ComplaintStatusActions
            complaint={c}
            roles={roles}
            onStatusChange={handleStatusChange}
            loading={loading === "pending"}
          />

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("employee")}
              </Typography>
              <Typography variant="body2">
                {`${c.employee?.givenName ?? ""} ${c.employee?.surName ?? ""}`.trim()}
                {c.employee?.passportNumber
                  ? ` (${c.employee.passportNumber})`
                  : ""}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("type")}
              </Typography>
              <Typography variant="body2">{te(c.type)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("priority")}
              </Typography>
              <Typography variant="body2">{te(c.priority)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("openedBy")}
              </Typography>
              <Typography variant="body2">{c.openedByEmail}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                {t("openedOn")}
              </Typography>
              <Typography variant="body2">
                {c.createdAt
                  ? format(new Date(c.createdAt), "dd-MMM-yyyy HH:mm")
                  : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {t("description")}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {c.description}
              </Typography>
            </Grid>
            {c.resolutionNotes && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {t("resolutionNotes")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {c.resolutionNotes}
                </Typography>
              </Grid>
            )}
          </Grid>

          {c.statusHistory?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t("statusHistory")}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell>{t("status")}</StyledTableCell>
                      <StyledTableCell>{t("changedBy")}</StyledTableCell>
                      <StyledTableCell>{t("date")}</StyledTableCell>
                      <StyledTableCell>{t("remark")}</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {[...c.statusHistory].reverse().map((entry, idx) => (
                      <StyledTableRow key={`${entry.changedAt}-${idx}`}>
                        <StyledTableCell>
                          <ComplaintStatusChip status={entry.status} />
                        </StyledTableCell>
                        <StyledTableCell>{entry.changedByEmail}</StyledTableCell>
                        <StyledTableCell>
                          {format(
                            new Date(entry.changedAt),
                            "dd-MMM-yyyy HH:mm",
                          )}
                        </StyledTableCell>
                        <StyledTableCell>{entry.remark ?? "-"}</StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <ComplaintFollowUp complaint={c} />
        </Paper>
      </Container>
      {success?.message && (
        <Toast severity="success">{success.message}</Toast>
      )}
      {error?.message && <Toast severity="error">{error.message}</Toast>}
    </>
  );
};

export default ComplaintDetail;
